import { APP_NAME, COMPANY_BRAND_NAME } from '../../support/brand';
import { COMPANY_CNPJ, COMPANY_NAME, COMPANY_EMAIL } from '../../support/legal';
import { PANEL_URL } from '../../support/publicUrls';
import { SAME_AS_LINKS } from '../../support/social';
import {
  CLIENTS,
  FAQS,
  HOME_MARKDOWN_URL,
  HOME_URL,
  LAST_MODIFIED,
  PARTNERS,
  SEO,
  SHOWCASES,
} from './content';

const showcaseImageUrl = (image) => `${PANEL_URL}${image}`;
export const SOCIAL_IMAGE_URL = `${PANEL_URL}/images/social/maia-home.png`;
const SOCIAL_IMAGE_ALT = 'MAIA — plataforma de IA para criar agentes';

export const META_TAGS = [
  ['meta', { name: 'description', content: SEO.description }],
  [
    'meta',
    { name: 'robots', content: 'index, follow, max-snippet:-1, max-image-preview:large' },
  ],
  ['meta', { property: 'og:type', content: 'website' }],
  ['meta', { property: 'og:locale', content: 'pt_BR' }],
  ['meta', { property: 'og:title', content: SEO.title }],
  ['meta', { property: 'og:description', content: SEO.description }],
  ['meta', { property: 'og:url', content: SEO.canonical }],
  ['meta', { property: 'og:image', content: SOCIAL_IMAGE_URL }],
  ['meta', { property: 'og:image:width', content: '1200' }],
  ['meta', { property: 'og:image:height', content: '630' }],
  ['meta', { property: 'og:image:alt', content: SOCIAL_IMAGE_ALT }],
  ['meta', { name: 'twitter:card', content: 'summary_large_image' }],
  ['meta', { name: 'twitter:title', content: SEO.title }],
  ['meta', { name: 'twitter:description', content: SEO.description }],
  ['meta', { name: 'twitter:image', content: SOCIAL_IMAGE_URL }],
  ['meta', { name: 'twitter:image:alt', content: SOCIAL_IMAGE_ALT }],
  ['link', { rel: 'canonical', href: SEO.canonical }],
  ['link', { rel: 'alternate', type: 'text/markdown', href: HOME_MARKDOWN_URL }],
];

export const STRUCTURED_DATA = [
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${HOME_URL}#organization`,
    name: COMPANY_BRAND_NAME,
    alternateName: 'OkMaia',
    legalName: COMPANY_NAME,
    taxID: COMPANY_CNPJ,
    url: HOME_URL,
    logo: `${HOME_URL}logo.png`,
    sameAs: SAME_AS_LINKS,
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'sales',
      email: COMPANY_EMAIL,
      url: 'https://wa.me/5521971792112',
      availableLanguage: 'pt-BR',
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: COMPANY_BRAND_NAME,
    url: HOME_URL,
    inLanguage: 'pt-BR',
    dateModified: LAST_MODIFIED,
    publisher: {
      '@id': `${HOME_URL}#organization`,
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: SEO.title,
    description: SEO.description,
    url: HOME_URL,
    inLanguage: 'pt-BR',
    dateModified: LAST_MODIFIED,
    about: {
      '@type': 'Thing',
      name: 'Plataforma de IA para criar agentes',
    },
    publisher: {
      '@id': `${HOME_URL}#organization`,
    },
    mentions: [...CLIENTS, ...PARTNERS].map((item) => ({
      '@type': 'Organization',
      name: item.name,
    })),
  },
  {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: APP_NAME,
    alternateName: 'ferramenta de agentes da MAIA',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    description: SEO.applicationDescription,
    url: HOME_URL,
    screenshot: SHOWCASES.map((showcase) => showcaseImageUrl(showcase.image)),
    provider: {
      '@id': `${HOME_URL}#organization`,
    },
    isPartOf: {
      '@id': `${HOME_URL}#organization`,
    },
    dateModified: LAST_MODIFIED,
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    })),
    dateModified: LAST_MODIFIED,
  },
];

function escapeAttribute(value) {
  return String(value).replaceAll('&', '&amp;').replaceAll('"', '&quot;');
}

export function renderHomeHeadTags() {
  const tags = META_TAGS.map(([tagName, attributes]) => {
    const serialized = Object.entries(attributes)
      .map(([key, value]) => `${key}="${escapeAttribute(value)}"`)
      .join(' ');
    return `<${tagName} data-home-static="true" ${serialized}>`;
  });

  tags.push(
    `<script data-home-static="true" type="application/ld+json">${JSON.stringify(
      STRUCTURED_DATA
    ).replaceAll('<', '\\u003c')}</script>`
  );

  return tags.join('\n    ');
}
